import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import EditableImage from '@/components/base/EditableImage';
import { homeServices } from '@/mocks/home';

interface HomeServiceDB {
  id: number;
  title: string;
  category_slug: string;
  description: string;
  image_key: string;
}

interface HomeServiceMerged {
  id: number;
  title: string;
  category_slug: string;
  description: string;
  image_key: string;
  image_url: string;
}

export default function ServicesSection() {
  const navigate = useNavigate();
  const [services, setServices] = useState<HomeServiceMerged[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchServices() {
      // Busca os dados textuais do banco
      const { data: dbData } = await supabase
        .from('home_services')
        .select('*')
        .order('sort_order');

      if (dbData && dbData.length > 0) {
        // Coleta todos os image_keys pra buscar as URLs de uma vez
        const keys = dbData.map((d: HomeServiceDB) => d.image_key).filter(Boolean);

        // Busca as URLs no site_images
        let imageMap: Record<string, string> = {};
        if (keys.length > 0) {
          const { data: imgData } = await supabase
            .from('site_images')
            .select('image_key, url')
            .in('image_key', keys);

          if (imgData) {
            imageMap = Object.fromEntries(
              imgData.map((img: { image_key: string; url: string }) => [img.image_key, img.url])
            );
          }
        }

        // Merge com fallback do mock de imagens
        const merged = (dbData as HomeServiceDB[]).map((item, idx) => {
          const mockFallback = homeServices[idx];
          return {
            id: item.id,
            title: item.title || mockFallback?.title || '',
            category_slug: item.category_slug || mockFallback?.categorySlug || '',
            description: item.description || mockFallback?.description || '',
            image_key: item.image_key || `home-service-${item.id}`,
            image_url: imageMap[item.image_key] || mockFallback?.image || 'https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/b2ef027698621a197bd775e73d4a1fe7.jpeg',
          };
        });
        setServices(merged);
      } else {
        // Fallback total pro mock quando o banco tá vazio
        const fallback = homeServices.map((s) => ({
          id: s.id,
          title: s.title,
          category_slug: s.categorySlug,
          description: s.description,
          image_key: `home-service-${s.id}`,
          image_url: s.image,
        }));
        setServices(fallback);
      }
      setLoading(false);
    }
    fetchServices();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-cream">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-14 md:mb-20">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-dark font-normal leading-tight mb-4">
            A Fotografia que <span className="italic text-warm-600">encanta</span>
          </h2>
          <p className="text-dark/60 text-base md:text-lg leading-relaxed">
            Histórias reais merecem ser guardadas com a alma.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start mb-16 md:mb-24">
          <div className="w-full lg:w-3/5 flex flex-col justify-center">
            <div className="space-y-6 text-dark/80 text-base md:text-lg leading-relaxed">
              <p>
                Seja bem-vindo!
                <br />
                Este é um espaço onde o tempo desacelera para dar lugar à sensibilidade e à intenção<span className="text-xl">.🫀</span>
              </p>
              <p>
                Para mim, a fotografia é uma herança visual. Meu trabalho é traduzir a essência espontânea de Casamentos, Famílias e Celebrações em registros elegantes e atemporais, daqueles que, daqui a muitos anos, ainda farão você sentir o calor exato de cada emoção.
              </p>
              <p>
                Mais do que técnica, coloco afeto em cada ação. Sei que ser fotografado exige entrega, por isso crio um ambiente de total conforto, leveza e segurança, onde você pode ser exatamente quem é. O resultado é um olhar sofisticado, mas profundamente humano.
              </p>
              <p className="font-serif italic text-warm-600">
                Vamos eternizar o seu momento mais bonito?
              </p>
            </div>
          </div>

          <div className="w-full lg:w-2/5">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-dark-light">
              <EditableImage
                imageKey="services-profile"
                src="https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/97660828694c62c5db6b0091bfc7f3d3.png"
                alt="Fotógrafo profissional"
                containerClassName="w-full h-full"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {services.map((service) => (
              <div
                key={service.id}
                className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-transform duration-500 hover:-translate-y-1"
                onClick={() => navigate(`/portfolio?categoria=${service.category_slug}`)}
              >
                <div className="relative h-72 md:h-80 overflow-hidden">
                  <EditableImage
                    imageKey={service.image_key}
                    src={service.image_url}
                    alt={service.title}
                    containerClassName="w-full h-full"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <span className="absolute bottom-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-dark">
                      {service.title}
                    </span>
                  </EditableImage>
                </div>
                <div className="p-5 md:p-6">
                  <p className="text-sm text-dark/70 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-warm-700 hover:text-warm-900 transition-colors">
                    Ver mais
                    <i className="ri-arrow-right-line text-xs" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}